import { Languages } from '@/common/utils/constants';
import { MembershipTypeSectionItemsInterface } from '@/widgets/BecomeAMember/components/Receipt/components/MembershipTypeSection/MembershipTypeSection.types';
import ReceiptDetailsTitleSection from '@/widgets/BecomeAMember/components/Receipt/components/common/ReceiptDetailsTitleSection/ReceiptDetailsTitleSection';
import { calculateDateWithBonus } from '@/widgets/BecomeAMember/components/Receipt/components/MembershipTypeSection/MembershipTypeSection.service';
import { calculateIncreasedDate } from '@/widgets/BecomeAMember/components/Receipt/utils/calculateIncreasedDate';
import { determineAdditionalText } from '@/widgets/BecomeAMember/components/Receipt/utils/determoneAdditionalText';
import { formatDate } from '@/widgets/BecomeAMember/components/Receipt/utils/formatDate';
import { formatPrice } from '@/common/utils/formatPriceForDisplay';
import { validateTermDuration } from '../../../../utils/validateTermDuration';

interface TermDurationProps extends MembershipTypeSectionItemsInterface {
  lang: Languages;
}

export default function TermDuration({
  lang,
  voucherData,
  currency,
  priceDecimalSeparator = '.',
  termData,
  startDate,
  translations,
}: TermDurationProps) {
  if (!termData && !voucherData) return null;

  const isRecurring = termData?.paymentFrequencyType === 'RECURRING';
  const isPeriodBased = voucherData?.voucher_effective_period === 'TIME_BASED';
  const isWithoutExtension =
    voucherData?.voucher_bonus_period &&
    voucherData?.voucher_bonus_period?.runtime_extension_type ===
    'WITHOUT_EXTENSION';

  const getMembershipPrice = () => {
    if (!voucherData) return termData?.rateStartPrice;

    if (voucherData.voucher_type === 'DISCOUNT') {
      return isPeriodBased
        ? voucherData.base_price
        : (voucherData.discounted_base_price ?? voucherData.base_price);
    }

    if (voucherData.voucher_type === 'COMPANY_FEE_SPLIT') {
      return isPeriodBased
        ? voucherData.member_amount_without_discount
        : voucherData.member_amount;
    }

    return termData?.rateStartPrice;
  };

  const membershipPrice = getMembershipPrice();
  const oldPrice = (() => {
    if (voucherData && !isPeriodBased) {
      if (
        voucherData.discount_value &&
        voucherData.voucher_type === 'COMPANY_FEE_SPLIT'
      ) {
        return formatPrice(voucherData?.discounted_base_price ?? '', priceDecimalSeparator);
      }

      if (voucherData.voucher_type === 'DISCOUNT' && voucherData.discount_value) {
        return formatPrice(voucherData.base_price ?? '', priceDecimalSeparator);
      }
    }
  })();

  // Default contract start date extracted
  const baseStartDate = startDate;

  // Calculating the end date of bonus period as the start date of term duration
  // or using default start date: today + bonusPeriod termValue and termUnit or today
  const startTermDate = calculateDateWithBonus({
    voucherData,
    startDate,
  });
  // Calculating the end date of voucher period: today + effective_period_time_based_term
  const periodBasedTermEndDate = voucherData?.effective_period_time_based_term
    ? calculateIncreasedDate({
      amount: Number(voucherData.effective_period_time_based_term?.value ?? 0),
      unit: voucherData.effective_period_time_based_term?.unit ?? '',
      startDate: baseStartDate,
    })
    : undefined;

  //start date:
  // 1) if period based only = periodBasedTermEndDate(today + effectivePeriodTimeBasedTerm)
  // 2) if period based and bonus = periodBasedTermEndDate(today + effectivePeriodTimeBasedTerm)
  // 3) if it's not period based = today date or calculated with bonus
  const rawStartMembershipTermDate = isPeriodBased
    ? periodBasedTermEndDate
    : startTermDate;

  //end date:
  // 1) if period based only = today + termValue period
  // 2) if period based and bonus = today + bonus + termValue period
  // 3) if it's not period based = today + termValue period
  const rawEndMembershipTermDate = calculateIncreasedDate({
    startDate: isWithoutExtension ? baseStartDate : startTermDate,
    amount: termData.termValue ?? 0,
    unit: termData.termUnit ?? '',
  });

  const additionalText = (start: string, end: string) => {
    const isTermEqualOrShorterThanFrequency =
      termData?.paymentFrequencyValue &&
      termData?.paymentFrequencyUnit &&
      validateTermDuration({
        start,
        end,
        termValue: termData?.term?.term,
        termUnit: termData?.term?.termUnit,
        providedPeriodValue: termData?.paymentFrequencyValue,
        providedPeriodmUnit: termData?.paymentFrequencyUnit,
      });

    if (isRecurring && (!isTermEqualOrShorterThanFrequency)) {
      return determineAdditionalText({
        startDate: start,
        endDate: end,
        termValue: String(termData?.paymentFrequencyValue),
        termUnit: termData?.paymentFrequencyUnit as string,
        translations,
      });
    }

    return `${start} - ${end}`;
  };

  const formattedStartTermRemainderDate = rawStartMembershipTermDate
    ? formatDate(rawStartMembershipTermDate, lang)
    : '';
  const formattedEndRemainderDate = rawEndMembershipTermDate
    ? formatDate(rawEndMembershipTermDate, lang)
    : '';

  const additionalRemainderText = additionalText(
    formattedStartTermRemainderDate,
    formattedEndRemainderDate
  );

  const isRemainderDisplay = isPeriodBased;

  return (
    <ReceiptDetailsTitleSection
      title={
        isRemainderDisplay
          ? translations['becomeamember.floatingreceipt.termdetails.remainder']
          : translations['becomeamember.floatingreceipt.termdetails.term']
      }
      currency={currency as string}
      oldPrice={oldPrice}
      additionalText={additionalRemainderText}
      price={formatPrice(membershipPrice ?? 0, priceDecimalSeparator)}
    />
  );
}
